// media-signature.ts — creatureMapTokenMediaSignature (hash for media-change
// detection) + syncCreatureTokenThumbMediaIfChanged (hot-swaps inner img/video
// when the media signature changes, preserves badges + drag bindings).
//
// Both functions are consumed by sibling files (create-element.ts imports
// creatureMapTokenMediaSignature; animate-entity.ts imports
// syncCreatureTokenThumbMediaIfChanged).

import type { EntityRow, PictureField } from './types';
import { encounterCreatureField } from '../shared/array-types';

/**
 * FT3D tokens: detect still-picture changes (e.g. presentation vs thumbnail swap from carousel).
 * Must match {@link renderCreatureThumbnail} with use_picture_thumbnail:false.
 */
function creatureMapTokenMediaSignature(c: EntityRow): string {
    const m = selectCreatureMedia(c, { use_picture_thumbnail: false }) as
        { type?: string; url?: string } | null | undefined;
    if (m?.url) {
        return `${m.type}:${m.url}`;
    }
    const picUrl = pictureField0(
        selectCreaturePicture(c, { use_picture_thumbnail: false }) as PictureField | null | undefined,
        'pic_url',
    );
    if (picUrl) {
        return `image:${picUrl}`;
    }
    const creatureLike = c as { encounter_creatures?: { encounter_creature_id?: ReadonlyArray<string> } };
    const id = encounterCreatureField(creatureLike.encounter_creatures, 'encounter_creature_id') ?? '';
    return `init:${id}`;
}

/**
 * Replace only the inner thumb/video/initials node — keeps badges, HP overlay, drag binding — AND
 * carry the fresh render's geometry (data-thumbnail_ dims + width_ft/height_ft + the wrapper's --tw/--th)
 * onto the live outer token. Returns TRUE when a real media/geometry swap happened, so the caller
 * (animateEntityChanges) can re-apply the token's zoom size + re-anchor it to the ground: a shapeshift
 * that flips the picture ASPECT RATIO (werewolf <-> man) otherwise left the token at the previous
 * form's shape, floating off the floor, until a full re-render or drag. Mirror of the encounter_update
 * path (websocket/encounter-update.ts). [Codex 2026-07-10]
 */
function syncCreatureTokenThumbMediaIfChanged(mapTokenEl: HTMLElement, entity: EntityRow): boolean {
    const wrap = mapTokenEl.querySelector<HTMLElement>('.position-relative.d-block.w-100.h-100');
    if (!wrap?.firstElementChild) {
        return false;
    }
    const nextSig = creatureMapTokenMediaSignature(entity);
    const prevSig = mapTokenEl.dataset.ftMapTokenMediaSig || '';

    // SSR-rendered tokens never set ftMapTokenMediaSig — first websocket push
    // would otherwise treat them as "media changed" and hot-swap the inner
    // <img>/<video>. The fresh node, parented under .object-prone, immediately
    // inherits the CSS :has()-driven rotate transition and replays the bouncy
    // "drop to prone" flip. Trust the page-load HTML on first sync (the SSR
    // token already carries the correct current-form geometry).
    if (!prevSig) {
        mapTokenEl.dataset.ftMapTokenMediaSig = nextSig;
        return false;
    }

    if (nextSig === prevSig) {
        return false;
    }

    const temp = document.createElement('div');
    temp.innerHTML = renderCreatureThumbnail(entity, { use_picture_thumbnail: false }).trim();
    const freshOuter = temp.firstElementChild as HTMLElement | null;
    const newWrap = freshOuter?.querySelector<HTMLElement>('.position-relative.d-block.w-100.h-100');
    const newMedia = newWrap?.firstElementChild;
    if (!freshOuter || !newMedia) {
        // Failed render — leave the OLD signature untouched so the next push retries (do NOT stamp).
        return false;
    }

    // Carry the fresh render's GEOMETRY onto the live outer token. renderCreatureThumbnail recomputes
    // these data-* from the NEW picture's aspect (creature-thumbnail.ts); the inner-node swap below only
    // replaces the <img>/<video>, so without this the token keeps the previous form's aspect ratio.
    for (const k of ['size', 'thumbnail_width', 'thumbnail_height', 'width_ft', 'height_ft'] as const) {
        const v = freshOuter.dataset[k];
        if (v !== undefined) {
            mapTokenEl.dataset[k] = v;
        }
    }
    // The wrapper's --tw/--th custom props drive the prone/status rotation transform's pivot box; copy
    // them so a rotated (prone) token doesn't keep the old form's box after the aspect changes.
    if (newWrap) {
        for (const prop of ['--tw', '--th'] as const) {
            const val = newWrap.style.getPropertyValue(prop);
            if (val) {
                wrap.style.setProperty(prop, val);
            }
        }
    }

    // Suppress the CSS transition on the cloned node so any :has()-driven
    // transform (e.g. prone rotate) commits instantly rather than replaying
    // its entrance animation on every legitimate media swap. The forced
    // reflow then restores transitions for future state changes.
    const clone = newMedia.cloneNode(true) as HTMLElement;
    clone.style.transition = 'none';
    wrap.replaceChild(clone, wrap.firstElementChild);
    void clone.offsetWidth;
    clone.style.transition = '';

    // Stamp the signature only AFTER a successful swap (a failed render above returns early with the
    // old sig intact, so the next push retries instead of being suppressed by a premature stamp).
    mapTokenEl.dataset.ftMapTokenMediaSig = nextSig;
    return true;
}

Object.assign(window, { creatureMapTokenMediaSignature, syncCreatureTokenThumbMediaIfChanged });

export { creatureMapTokenMediaSignature, syncCreatureTokenThumbMediaIfChanged };
